import { useEffect, useRef } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import { components } from "../../design/tokens";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { ImageService } from "../../services/imageService";
import { trackPageview } from "../../utils/analyticsUtils";
import {
  useIconImageToCrop,
  useIsIconUploading,
  useProjectIconActions,
  useShouldShowIconCropper,
  useUploadedIconImage,
} from "../../view_stores/useProjectIconStore";
import { useProjectStatus } from "../../view_stores/useProjectStatusStore";
import ProjectIconContainer from "./components/ProjectIconContainer";
import ProjectIconCroppingModal from "./components/ProjectIconCroppingModal";
import ProjectIconUploadedImageActions from "./components/ProjectIconUploadedImageActions";

export default function ProjectIcon() {
  const uploadedImage = useUploadedIconImage();
  const imageToCrop = useIconImageToCrop();
  const showCropper = useShouldShowIconCropper();
  const projectStatus = useProjectStatus();

  const {
    initialize,
    setIsUploading,
    setImageToCrop,
    setShowCropper,
    handleCropConfirm,
    handleCropCancel,
  } = useProjectIconActions();
  const { showSnackbar } = useSnackbar();

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    trackPageview("Workspace Icon");
    if (projectStatus !== null) {
      initialize();
    }
  }, [projectStatus, initialize]);

  const processImageFile = async (file: File) => {
    setIsUploading(true);
    try {
      // Validate image resolution first
      const validation = await ImageService.validateIconImageResolution(file);

      if (!validation.isValid) {
        showSnackbar(validation.error || "Invalid image resolution", {
          type: "error",
        });
        return false;
      }

      // Compress image if needed
      const compressedImg = await ImageService.compressImageIfNeeded(file);
      // Convert to base64 for display
      const base64 = await ImageService.convertImageToBase64(compressedImg);
      // Create full data URL for the cropper
      const dataUrl = `data:${compressedImg.type};base64,${base64}`;
      setImageToCrop(dataUrl);
      setShowCropper(true);
      return true;
    } catch (error) {
      console.error("Error processing image:", error);
      showSnackbar("Error processing image. Please try again.", {
        type: "error",
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageDropped = async (file: File) => {
    await processImageFile(file);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const success = await processImageFile(file);
      if (!success) {
        // Clear the file input on error
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onCropConfirm = (croppedImage: File) => {
    handleCropConfirm(croppedImage);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onCropCancel = () => {
    handleCropCancel();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <div className="flex flex-col gap-10 relative h-full">
        <div className="flex items-center justify-center relative">
          <ProjectIconContainer imageDropped={handleImageDropped} />
        </div>
        <div className="mx-auto flex items-center gap-10">
          {uploadedImage ? (
            <ProjectIconUploadedImageActions />
          ) : (
            <UploadBtn onClick={handleUploadClick} />
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          onChange={handleImageUpload}
          className="hidden"
        />

        {/* Cropping Modal */}
        {showCropper && imageToCrop && (
          <ProjectIconCroppingModal
            originalImage={imageToCrop}
            onConfirm={onCropConfirm}
            onCancel={onCropCancel}
          />
        )}
      </div>
      <Tooltip
        id="icon-size-tooltip"
        place="top"
        className="max-w-[95%]"
        opacity={1.0}
      >
        Minimum required size is 512×512 pixels and maximum is 4096×4096 pixels.
      </Tooltip>
    </>
  );
}

function UploadBtn({ onClick }: { onClick: () => void }) {
  const isUploading = useIsIconUploading();

  return (
    <button
      id="project-icon-upload"
      className={`${components.button.primary} gap-3`}
      onClick={onClick}
      disabled={isUploading}
    >
      {isUploading && <LuLoaderCircle className="animate-spin" size={20} />}
      {isUploading ? "Uploading..." : "Upload image"}
    </button>
  );
}
